<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    :class="classes"
    :style="styles"
  >
    <div class="base-checkbox--holder">
      <div
        class="base-checkbox--input"
        :tabindex="disabled ? -1 : 0"
        @click="update"
        @keyup.space="update"
      >
        <span
          :class="{
            'base-checkbox--input-icon': true,
            'base-checkbox--input-icon-checked': value && !indeterminate,
            'base-checkbox--input-icon-indeterminate': indeterminate,
            'base-checkbox--input-icon-disabled': props.disabled === true,
          }"
        >
          <span v-if="indeterminate"><span><i class="fa fa-minus" /></span></span>
          <span v-else-if="value"><span><i class="far fa-check" /></span></span>
        </span>
      </div>
      <label
        v-if="hasLabel"
        class="base-checkbox--label"
        :for="componentName"
        @click.prevent="clickHandler"
      >
        <div
          v-if="label"
          @click.prevent="clickHandler"
          v-html="label"
        />
        <slot v-else />
      </label>
    </div>
    <div
      v-if="error"
      class="base-checkbox--error"
    >
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue';
import { getColor } from '@/helper';

const props = defineProps<{
  value?: boolean | number;
  label?: string | number;
  disabled?: boolean;
  name?: string | number;
  color?: string;
  hoverColor?: string;
  error?: string;
  indeterminate?: boolean;
}>();

const emit = defineEmits(['update', 'input']);
const componentName = computed<string>(() => props.name as string);

const slots = useSlots();

const classes = computed(() => ({
  'base-checkbox': true,
  error: props.error,
}));

const styles = computed(() => ({
  '--checkbox-color': getColor(props.color, 'company-main-primary'),
  '--checkbox-hover-color': getColor(props.hoverColor, props.color || 'company-main-primary_hover'),
}));

const hasLabel = computed(() => `${props.label || ''}`.trim() || slots.default);

const update = () => {
  if (props.disabled === true) {
    return;
  }

  if (typeof props.value === 'number') {
    emit('update', props.value ? 0 : 1);
    emit('input', props.value ? 0 : 1);
  } else {
    emit('update', !props.value);
    emit('input', !props.value);
  }
};

const clickHandler = (e: MouseEvent) => {
  e.stopPropagation();
  const { target } = e as any;
  if (target?.tagName === 'A') {
    window.open(target.href, '_blank');
  } else {
    update();
  }
};

defineExpose({
  componentName,
  classes,
  styles,
  hasLabel,
  update,
  clickHandler,
});
</script>

<style lang="scss" scoped>
.base-checkbox {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    z-index: 1;
    padding-right: 10px;

    &--holder {
        display: flex;
    }

    &--input {
        background-color: #fff;
        display: inline-block;
        min-width: 1.5rem;
        height: 1.5rem;
        border: 1px solid #dee2e6;
        border-radius: 5px;
        overflow: hidden;

        &:hover {
            box-shadow: 0 0 2px 0 var(--checkbox-hover-color);
        }
    }

    &--input-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
        opacity: 0;

        &-checked {
            opacity: 1;
            background-color: var(--checkbox-color);
            color: #fff;
        }

        &-indeterminate {
            opacity: 1;
            background-color: var(--checkbox-color);
            color: #fff;
        }
        &-disabled {
            opacity: 1;
            background-color: var(--gray-light);
            cursor: not-allowed;
        }

    }

    &--label {
        padding-left: .75rem;
        width: -webkit-fill-available;
        width: -moz-available;
        a {
            color: var(--checkbox-color);
        }
    }

    &--error {
        display: block;
        font-size: 12px;
        color: var(--bs-danger);
        line-height: 1.25;
        padding-top: .5rem;
    }

    &.error {
        .base-checkbox--input {
            box-shadow: 0px 0px 5px 0px var(--bs-danger);
        }
    }
}
</style>
